import * as R from "ramda";
import {
  takeEvery,
  all,
  fork,
  put,
  call,
  select,
  delay
} from "redux-saga/effects";
import { getters, actions } from "./model";
import { apiCall } from "App/Data/sagas";
import { orgId as getOrgId } from "redux/modules/organization/selectors";
import {
  AUTH_FORM_SETUP,
  getAuthenticationUrl,
  STEP_IDS,
  FORM_ID,
  SOURCE_IDS,
  getConnectionEventMappingsUrl,
  getSyncEventsUrl
} from "./constants";
import {
  actions as FormActions,
  getters as FormGetters
} from "ui-kit/Form/model";
import { showSnackbar } from "redux/modules/snackbar/actions";
import { ERROR_MESSAGE } from "./constants";
import { registerError } from "redux/modules/errors/actions";

const init = function*() {};

/**
 * Load Integration Connection/Auth Settings
 */
const loadConnectionSettings = function*() {
  try {
    const integrationId = yield select(getters.selectedSourceId);
    const orgId = yield select(getOrgId);
    const { payload: authPayload } = yield call(apiCall, {
      method: "get",
      url: getAuthenticationUrl(integrationId, orgId)
    });

    if (integrationId === SOURCE_IDS.ON24) {
      yield put(actions.setSelectedConnectionConfigId(authPayload.id));
    }

    if (Object.keys(authPayload).includes("is_connected")) {
      yield put(actions.setConnectionSettings(authPayload.connection_settings));
      yield put(actions.setIsConnected(authPayload.is_connected));
    } else {
      yield put(actions.setConnections(authPayload));
    }
  } catch (error) {
    yield put(
      showSnackbar({
        message: "There was an issue getting connection settings"
      })
    );
  }
};

/**
 * Process adding a connection
 */
const processConnectionCreation = function*() {
  try {
    yield put(actions.setSaving(true));

    const integrationId = yield select(getters.selectedSourceId);
    const orgId = yield select(getOrgId);
    const fields = yield select(FormGetters.fields, {
      instanceId: FORM_ID
    });

    // Build connection validation
    const data = {};
    let triggerError = false;

    Object.entries(AUTH_FORM_SETUP[integrationId]).forEach(([key, value]) => {
      const fieldValue = R.path([key, "value"])(fields);

      if (R.isNil(fieldValue)) {
        triggerError = true;
        return;
      }

      data[value.key] = R.path([key, "value"])(fields);
    });

    // Send back error and bail if we encounter one
    if (triggerError) {
      yield put(showSnackbar({ message: ERROR_MESSAGE }));
      yield put(actions.setSaving(false));
      return false;
    }

    // Attempt to save connection
    const { payload: connectionPayload } = yield call(apiCall, {
      method: "post",
      url: getAuthenticationUrl(integrationId, orgId),
      data
    });

    yield put(showSnackbar({ message: "Connection Created" }));
    yield put(actions.setSaving(false));
    yield put(actions.setSelectedConnectionConfigId(null));
    yield put(actions.setShowNewConnectionForm(false));

    yield call(loadConnectionSettings);

    yield put(actions.setSelectedConnectionId(connectionPayload.id));

    return true;
  } catch (error) {
    yield put(
      showSnackbar({
        message:
          error.body?.message ||
          "There was an issue connecting your integration"
      })
    );
    yield put(actions.setSaving(false));
    yield put(actions.setIsNewConnectionValid(false));
    return false;
  }
};

/**
 * Process removing a connection
 */
const processConnectionRemoval = function*({ payload: { configId } }) {
  try {
    yield put(actions.setSaving(true));
    const integrationId = yield select(getters.selectedSourceId);
    const orgId = yield select(getOrgId);
    yield call(apiCall, {
      method: "delete",
      url: `${getAuthenticationUrl(integrationId, orgId)}/${configId}`
    });

    yield put(showSnackbar({ message: "Connection Removed" }));
    yield call(loadConnectionSettings);
    yield put(actions.setSaving(false));
  } catch (error) {
    yield put(
      showSnackbar({
        message:
          error.body?.message || "There was an issue removing your connection"
      })
    );
    yield put(actions.setSaving(false));
  }
};

/**
 * Process loading connections
 */
const processConnectionLoading = function*() {
  try {
    yield put(actions.setLoadingSettings(true));

    const integrationId = yield select(getters.selectedSourceId);
    const orgId = yield select(getOrgId);
    const { payload: authPayload } = yield call(apiCall, {
      method: "get",
      url: getAuthenticationUrl(integrationId, orgId)
    });

    yield all([
      put(actions.setIsConnected(authPayload.is_connected)),
      put(actions.setConnectionSettings(authPayload.connection_settings))
    ]);
  } catch (error) {
    yield put(
      showSnackbar({
        message: "There was an issue getting your connections"
      })
    );
  } finally {
    yield put(actions.setLoadingSettings(false));
  }
};

/**
 * Prepare the edit form for editing an existing connection
 */
const processSetEditConnection = function*({ payload: { connection } }) {
  const integrationId = yield select(getters.selectedSourceId);
  yield put(actions.setIsEditingConnection(true));

  yield all(
    Object.entries(AUTH_FORM_SETUP[integrationId]).map(([key, value]) =>
      put(
        FormActions.setFieldValue(
          connection ? connection[key] : value.iniValue,
          {
            meta: {
              instanceId: FORM_ID,
              fieldId: key,
              fieldType: value.type
            }
          }
        )
      )
    )
  );

  yield put(actions.setShowNewConnectionForm(true));
};

/**
 * Load Integration Event Mappings
 */
const loadConnectionEventMappings = function*() {
  try {
    const integrationId = yield select(getters.selectedSourceId);
    const orgId = yield select(getOrgId);
    const selectedConnectionId = yield select(getters.selectedConnectionId);

    const { payload: mappingPayload } = yield call(apiCall, {
      method: "get",
      url: getConnectionEventMappingsUrl(
        integrationId,
        orgId,
        selectedConnectionId
      )
    });

    const externalEventMappings = mappingPayload.mappings.reduce((acc, m) => {
      if (m.lennd_event_id) {
        acc[m.external_event_id] = String(m.lennd_event_id);
      }
      return acc;
    }, {});

    yield all([
      put(
        actions.setSessionboardEvents(
          mappingPayload.sessionboard_events.map(e => ({
            ...e,
            value: String(e.id),
            label: e.name
          }))
        )
      ),
      put(
        actions.setIntegrationEvents(
          mappingPayload.integration_events.map(e => ({
            ...e,
            value: String(e.id),
            label: e.name
          }))
        )
      ),
      put(actions.setEventMappings(mappingPayload.mappings)),
      put(actions.setExternalEventMappings(externalEventMappings)),
      put(
        actions.setSelectedExternalEventIds(
          mappingPayload.mappings.map(m => m.external_event_id)
        )
      )
    ]);
  } catch (error) {
    yield put(
      showSnackbar({
        message: "There was an issue getting connection event mappings"
      })
    );
  }
};

/**
 * Process Syncing Events
 */
const processSyncEvents = function*() {
  try {
    const integrationId = yield select(getters.selectedSourceId);
    const orgId = yield select(getOrgId);
    const selectedConnectionId = yield select(getters.selectedConnectionId);

    const { payload: syncPayload } = yield call(apiCall, {
      method: "post",
      url: getSyncEventsUrl(integrationId, orgId, selectedConnectionId)
    });

    yield delay(200);

    let progressResult = yield apiCall({
      method: "get",
      url: `/jobs/progress/${syncPayload.job_id}`
    });

    while (["pending", "processing"].includes(progressResult.payload.status)) {
      yield delay(1000);
      progressResult = yield call(apiCall, {
        method: "get",
        url: `/jobs/progress/${syncPayload.job_id}`
      });
    }

    if (progressResult.payload.status === "error") {
      yield all([
        put(
          registerError([
            {
              system: "An error occurred syncing events",
              user: "An error occurred syncing events"
            }
          ])
        )
      ]);
      return false;
    }

    return true;
  } catch (error) {
    yield put(
      showSnackbar({
        message: "There was an issue syncing events"
      })
    );
    return false;
  }
};

/**
 * Save Event Mappings
 */
const saveEventMappings = function*() {
  try {
    const integrationId = yield select(getters.selectedSourceId);
    const orgId = yield select(getOrgId);
    const selectedConnectionId = yield select(getters.selectedConnectionId);

    const selectedExternalEventIds = yield select(
      getters.selectedExternalEventIds
    );
    const externalEventMappings = yield select(getters.externalEventMappings);

    yield call(apiCall, {
      method: "put",
      url: getConnectionEventMappingsUrl(
        integrationId,
        orgId,
        selectedConnectionId
      ),
      data: {
        selectedExternalEventIds,
        externalEventMappings
      }
    });
  } catch (error) {
    yield put(
      showSnackbar({
        message: "There was an issue saving event mappings"
      })
    );
  }
};

const goToPreviousStep = function*() {
  const stepId = yield select(getters.stepId);
  if (stepId === STEP_IDS.SELECT_SOURCE) {
    // handle: cancel
    yield put(actions.hideModal());
  } else if (stepId === STEP_IDS.CONNECT) {
    // handle: go back to select source
    yield put(actions.setStepId(STEP_IDS.SELECT_SOURCE));
  } else if (stepId === STEP_IDS.PREVIEW) {
    // handle: go back to connect
    yield put(actions.setStepId(STEP_IDS.CONNECT));
  } else if (stepId === STEP_IDS.IMPORT) {
    // handle: go back to select source
    yield put(actions.setStepId(STEP_IDS.PREVIEW));
  }
};

const goToNextStep = function*() {
  const stepId = yield select(getters.stepId);
  if (stepId === STEP_IDS.SELECT_SOURCE) {
    // handle: go to connect
    yield put(actions.setLoadingSettings(true));
    yield put(actions.setSelectedConnectionId(null));
    yield put(actions.setStepId(STEP_IDS.CONNECT));
    // yield call(processConnectionLoading);
    yield call(loadConnectionSettings);
    yield put(actions.setLoadingSettings(false));
  } else if (stepId === STEP_IDS.CONNECT) {
    // handle: go to preview
    yield put(actions.setLoadingSettings(true));
    yield put(actions.setStepId(STEP_IDS.PREVIEW));
    yield call(loadConnectionEventMappings);
    yield put(actions.setLoadingSettings(false));
  } else if (stepId === STEP_IDS.PREVIEW) {
    // handle: go to import
    yield call(saveEventMappings);
    yield put(actions.setStepId(STEP_IDS.IMPORT));
    const result = yield call(processSyncEvents);
    if (!result) {
      // handle: go back to preview if there was an error syncing events
      yield put(actions.setStepId(STEP_IDS.PREVIEW));
      yield put(actions.setLoadingSettings(true));
      yield call(loadConnectionEventMappings);
      yield put(actions.setLoadingSettings(false));
    }
  } else {
    // handle: done
    yield put(actions.hideModal());
  }
};

const watchInit = function*() {
  yield takeEvery(actions.init.type, init);
};

const watchGoToPreviousStep = function*() {
  yield takeEvery(actions.goToPreviousStep.type, goToPreviousStep);
};

const watchGoToNextStep = function*() {
  yield takeEvery(actions.goToNextStep.type, goToNextStep);
};

const watchLoadConnections = function*() {
  yield takeEvery(actions.loadConnections.type, processConnectionLoading);
};

const watchAddConnection = function*() {
  yield takeEvery(actions.addConnection.type, processConnectionCreation);
};

const watchRemoveConnection = function*() {
  yield takeEvery(actions.removeConnection.type, processConnectionRemoval);
};

const watchSetEditConnection = function*() {
  yield takeEvery(actions.setEditConnection.type, processSetEditConnection);
};

const rootSaga = function*() {
  yield all([
    fork(watchInit),
    fork(watchGoToPreviousStep),
    fork(watchGoToNextStep),
    fork(watchLoadConnections),
    fork(watchAddConnection),
    fork(watchRemoveConnection),
    fork(watchSetEditConnection)
  ]);
};

export default rootSaga;
